import GoogleMap from 'google-maps-react-markers';
import Link from 'next/link';
import { useState } from 'react';
import PostHog from '../analytics/PostHog';
import { Button } from 'components/Button';
import { DEFAULT_MAP_OPTIONS } from 'features/JobMap/map.constants';

const LocationInformation = ({ location, employer }) => {
  if (!location) {
    return (
      <div className="overflow-hidden bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">Click a location to see the exact address.</div>
          </dl>
        </div>
      </div>
    );
  }

  return (
    <div id={location.id} className="overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Location Information</h3>
      </div>
      <div className="px-4 py-5 border-t border-gray-200 sm:px-6">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Name</dt>
            <dd className="mt-1 text-sm text-gray-900">{location.name}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Address</dt>
            <dd className="mt-1 text-sm text-gray-900">{location.address}</dd>
          </div>
          {employer?.slug && (
            <div className="sm:col-span-2">
              <PostHog
                eventName="Click View more jobs at this location [Job Listing]"
                clone={false}
              >
                <Link href={`/employer/${employer?.slug}`}>
                  <Button className="md:mt-6">View more jobs at this location</Button>
                </Link>
              </PostHog>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
};

const MarkerComponent = ({ location, selectedLocation, onClick }) => {
  const isActive = location.id === selectedLocation?.id;

  return (
    <PostHog
      eventName="Click on a map pointer [Specific Job Page]"
      properties={{ Address: location.address, 'Location ID': location.id }}
    >
      <div class="hover:cursor-pointer" onClick={() => onClick(location)}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-7 h-7"
          viewBox="0 0 20 20"
          fill={isActive ? '#006AFD' : '#FC663D'}
        >
          <path
            fillRule="evenodd"
            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </PostHog>
  );
};

// Return map bounds based on list of places
const getMapBounds = (map, maps, places) => {
  const bounds = new maps.LatLngBounds();

  places.forEach((place) => {
    bounds.extend(new maps.LatLng(place.lat, place.lng));
  });
  return bounds;
};

// Re-center map when resizing the window
const bindResizeListener = (map, maps, bounds) => {
  maps.event.addDomListenerOnce(map, 'idle', () => {
    maps.event.addDomListener(window, 'resize', () => {
      map.fitBounds(bounds);
    });
  });
};

// Fit map to its bounds after the api is loaded
const apiIsLoaded = (map, maps, places) => {
  // Get bounds by our places
  const bounds = getMapBounds(map, maps, places);
  // Fit map to bounds
  map.fitBounds(bounds);
  const zoom = map.getZoom();
  if (zoom > 15) {
    map.setZoom(15);
  }
  // Bind the resize listener
  bindResizeListener(map, maps, bounds);
};

export default function MultipleMarkerMap({ locations, employer }) {
  const [selectedLocation, setSelectedLocation] = useState(
    locations?.length == 1 ? locations[0] : null,
  );

  if (!locations || !locations.length) {
    return null;
  }
  const coords = locations
    .map((loc) => {
      return { id: loc.id, lat: loc.lat, lng: loc.lng };
    })
    .filter((loc) => loc.lat && loc.lng);

  const onClick = (location) => {
    const selected = locations.find((loc) => loc.id === location.id);
    setSelectedLocation(selected);
    setTimeout(() => {
      const selectedDescription = document.getElementById(selected.id);
      if (selectedDescription.scrollIntoViewIfNeeded) {
        selectedDescription.scrollIntoViewIfNeeded(false);
      } else {
        selectedDescription.scrollIntoView(false);
      }
    }, 200);
  };

  const firstLocation = coords.find((loc) => loc.lat && loc.lng);

  if (!firstLocation) {
    return <small>Map Failed to load</small>;
  }

  return (
    <div className="block md:grid md:grid-cols-2">
      <div id="locationsMap" className="w-auto h-[400px]">
        <GoogleMap
          apiKey="AIzaSyANRjtipx5m9FdoFrYcJikOCuNrtV45BHY"
          libraries={['geometry']}
          defaultCenter={{ lat: parseInt(firstLocation?.lat), lng: parseInt(firstLocation?.lng) }}
          defaultZoom={10}
          onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps, coords)}
          className="w-full h-full"
          options={DEFAULT_MAP_OPTIONS}
        >
          {locations.map((location, index) => (
            <MarkerComponent
              key={index}
              selectedLocation={selectedLocation}
              onClick={onClick}
              lat={location.lat}
              lng={location.lng}
              location={location}
            />
          ))}
        </GoogleMap>
      </div>
      <LocationInformation location={selectedLocation} employer={employer} />
    </div>
  );
}
